/**
 * TikTokApprovalListener.jsx
 * 
 * Global component that listens for pending TikTok post approvals
 * and shows a notification badge/banner when there are posts waiting.
 * 
 * Usage: Place this in App.jsx or a layout component
 * <TikTokApprovalListener />
 */

import { useState, useEffect, useCallback } from "react";
import { useAuth, apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Bell, X, Check, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import TikTokPostApprovalModal from "./TikTokPostApprovalModal";

export default function TikTokApprovalListener() {
  const { user } = useAuth();
  const [pendingPosts, setPendingPosts] = useState([]);
  const [showBanner, setShowBanner] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Check for pending approvals
  const checkPendingApprovals = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const response = await apiClient.get("/social/tiktok/pending-approvals");
      const posts = response.data?.posts || [];
      
      setPendingPosts(posts);
      
      // Show banner if there are pending posts
      if (posts.length > 0) {
        setShowBanner(true);
        
        // Show toast notification for new pending posts
        if (posts.length === 1) {
          toast.info("TikTok Approval Needed", {
            description: "You have a post waiting for TikTok approval",
            action: {
              label: "Review",
              onClick: () => {
                setSelectedPost(posts[0]);
                setShowModal(true);
              }
            }
          });
        } else {
          toast.info(`${posts.length} Posts Need TikTok Approval`, {
            description: "Tap to review and approve your scheduled posts"
          });
        }
      }
    } catch (error) {
      // Silently fail - this is a background check
      console.log("Failed to check TikTok pending approvals:", error);
    }
  }, [user?.id]);
  
  // Check on mount and periodically
  useEffect(() => {
    if (!user?.id) return;
    
    // Initial check
    checkPendingApprovals();
    
    // Check every 5 minutes
    const interval = setInterval(checkPendingApprovals, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [user?.id, checkPendingApprovals]);
  
  // Handle approval completion
  const handleApprovalComplete = () => {
    setShowModal(false);
    setSelectedPost(null);
    checkPendingApprovals(); // Refresh the list
  };
  
  // Don't render if no user or no pending posts
  if (!user?.id || pendingPosts.length === 0) return null;
  
  return (
    <>
      {/* Floating notification banner */}
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ opacity: 0, y: -20, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: -20, x: "-50%" }}
            className="fixed top-20 left-1/2 z-50 w-full max-w-md"
          >
            <div className="mx-4 bg-gradient-to-r from-black via-viraly-card to-black border border-viraly-orange/30 rounded-xl p-4 shadow-lg shadow-viraly-orange/10">
              <div className="flex items-start gap-3">
                {/* TikTok Icon */}
                <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center flex-shrink-0 border border-white/10">
                  <span className="text-sm font-bold text-white">TT</span>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm">TikTok Approval Needed</h4>
                    <button
                      onClick={() => setShowBanner(false)}
                      className="text-viraly-muted hover:text-white p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-viraly-muted mt-1">
                    {pendingPosts.length === 1 
                      ? "1 scheduled post needs your approval before posting to TikTok"
                      : `${pendingPosts.length} scheduled posts need your approval`
                    }
                  </p>
                  
                  <div className="flex items-center gap-2 mt-3">
                    <Button
                      size="sm"
                      className="bg-viraly-orange hover:bg-viraly-orange/90 text-xs h-8"
                      onClick={() => {
                        setSelectedPost(pendingPosts[0]);
                        setShowModal(true);
                      }}
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Review Now
                    </Button>
                    {pendingPosts.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs h-8 text-viraly-muted"
                        onClick={() => window.location.href = "/posts?tab=scheduled&filter=tiktok_pending"}
                      >
                        View All ({pendingPosts.length})
                        <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Approval Modal */}
      {showModal && selectedPost && (
        <TikTokPostApprovalModal
          post={selectedPost}
          onApprove={handleApprovalComplete}
          onReject={handleApprovalComplete}
          onClose={() => {
            setShowModal(false);
            setSelectedPost(null);
          }}
        />
      )}
    </>
  );
}


/**
 * TikTokApprovalBadge - Small badge for navigation showing pending count
 * Usage: <TikTokApprovalBadge />
 */
export function TikTokApprovalBadge() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!user?.id) return;
    
    const checkCount = async () => {
      try {
        const response = await apiClient.get("/social/tiktok/pending-approvals");
        setCount(response.data?.pending_count || 0);
      } catch {
        setCount(0);
      }
    };
    
    checkCount();
    const interval = setInterval(checkCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user?.id]);
  
  if (count === 0) return null;
  
  return (
    <span className="absolute -top-1 -right-1 w-5 h-5 bg-viraly-orange text-white text-xs font-bold rounded-full flex items-center justify-center">
      {count > 9 ? "9+" : count}
    </span>
  );
}
